import {connect} from 'pwa-helpers';
import {LitElement, html, css} from 'lit';
import {customElement} from 'lit/decorators/custom-element.js';
import {msg} from '@lit/localize';
import {repeat} from 'lit/directives/repeat.js';
import {when} from 'lit/directives/when.js';
import {classMap} from 'lit/directives/class-map.js';
import {property} from 'lit/decorators/property.js';

import store from '../store';

import {
	selectBasicSpcAttributes,
	selectBasicSpcAttributesSatisfactionPercentage,
	selectBasicSpcAttributesSatisfied,
	selectDetailSpcAttributes,
	selectDetailSpcAttributesSatisfactionPercentage, selectFilteredSpcAttributes,
	selectFirstSection,
	selectProductData, selectSpcAttributes, selectSpcComponents, selectSpcConfiguration, selectSpcConfigurations,
	selectSpcIsSatisfied,
	selectUnfilteredBasicSpcAttributes,
	selectUnfilteredDetailSpcAttributes,
	selectUnFilteredSpcAttributes,
	selectUnsatisfiedSpcAttributes
} from '../selectors';
import {stringToAttributeId} from './spc/util/spcUtils';
import {setDownloadPanelSpecsheetActive} from '../slices/statusSlice';
import {conditional} from '../util/conditional';
import {pimText, pimTextDebug} from '../util/values';

@customElement('attributes-view')
export class AttributesView extends connect(store)(LitElement) {

	static styles = css`
		:host {
			height: 100%;
			padding: 0;
		}

		@media (max-width: 768px) {
			:host {
				padding: 0 var(--small-padding);
			}
		}

		:host::-webkit-scrollbar {
			display: none;
		}

		.header {
			font: var(--font-widget-label);
			color: var(--color-grey4);
			padding-bottom: var(--very-small-padding);
			margin-bottom: var(--padding);
			border-bottom: 1px solid var(--color-white);
			break-inside: avoid;
			break-after: avoid;
			position: relative;

		}

		.attributes {
			width: 100%;
			height: 100%;
			padding: var(--large-padding);
			box-sizing: border-box;
		}

		.attributes.columns {
			column-width: var(--features-column-width, 360px);
			column-count: 6;
			column-gap: var(--features-column-gap, 48px) ;
			column-fill: balance;
		}
		.attributes.columns.triggerReflow {
			column-fill: auto !important;
		}
		.attributes.columns .attribute {
			break-inside: avoid-column;
			padding-bottom: var(--features-column-gap, 48px);
		}

		.attributes.grid {
			display: grid;
			grid-auto-flow: row dense;
			gap: var(--features-column-gap, 48px);
			grid-template-columns: repeat(auto-fit, minmax(var(--features-column-width), 1fr));
		}

		.attribute {
			position: relative;
			break-inside: avoid;
		}
		.widget {
			position: relative;
			break-inside: avoid;
		}
		.widget>* {
			margin-bottom: var(--large-padding);
			display: block;
		}

		.progressButton {
			display: none;
		}

		@media (max-width: 768px) {
			.progressButton {
				display: block;
				padding-bottom: var(--features-column-gap);
			}
		}
	`;

	constructor() {
		super();
		store.subscribe(() => this.requestUpdate());
	}

	stateChanged(state) {
		this.state = state;
		this.section = selectFirstSection(state);
		this.debugShowAll = state.status.debugShowAll;
		this.productData = selectProductData(state);

		this.region = state.status.region;
		this.lang = state.status.lang;
		if (this.debugShowAll) {
			this.basicSpcAttributes = selectUnfilteredBasicSpcAttributes(state); //selectUnfilteredBasicSpcAttributes(state);
			this.detailSpcAttributes = selectUnfilteredDetailSpcAttributes(state);
		} else {
			this.basicSpcAttributes = selectBasicSpcAttributes(state);
			this.detailSpcAttributes = selectDetailSpcAttributes(state);
		}
		this.unsatistfiedSpcAttributes = []; // selectUnsatisfiedSpcAttributes(state);
		this.basicPercentage = selectBasicSpcAttributesSatisfactionPercentage(state);
		this.detailPercentage = selectDetailSpcAttributesSatisfactionPercentage(state);
		this.fullPercentage = Math.ceil((this.basicPercentage + this.detailPercentage) / 2);
		this.isBasicSatisfied = selectBasicSpcAttributesSatisfied(state);
		this.spcIsSatisfied = selectSpcIsSatisfied(state);
		// console.log(this.spcAttributes, this.basicSpcAttributes, this.detailSpcAttributes);
		this.spcConfiguration = selectSpcConfiguration(state);
		this.conditionalVariantValues = state.productData.conditionalVariantValues;
	}
	c(id) {
		return conditional(this.spcConfiguration, this.conditionalVariantValues, id);
	}
	t(featureId, context, value, prefix, suffix) {
		return this.debug ?
			pimTextDebug(this.lang, featureId, context, this.spcConfiguration, this.state.productData.translations, value, prefix, suffix) :
			pimText(this.lang, featureId, context, this.spcConfiguration, this.state.productData.translations, value, prefix, suffix);
	}

	showSpecsheet(type) {
		store.dispatch(setDownloadPanelSpecsheetActive({downloadPanelSpecsheetActive: true, type: type}));
	}

	connectedCallback() {
		super.connectedCallback();
		if (!navigator.userAgent.toLowerCase().includes('firefox')) return;
		setTimeout(() => {
			this.shadowRoot.querySelector('.attributes').classList.add('triggerReflow');
			setTimeout(() => {
				 this.shadowRoot.querySelector('.attributes').classList.remove('triggerReflow');
			}, 20);
		}, 1000);
	}

	render() {
		if (!this.basicSpcAttributes) return null;
		const zindex = 100;
		return html`
			<div class="attributes columns" id="attributesReference">
				<div class="header" style="z-index: ${zindex}">${msg('Basic Specsheet')}</div>
				${when(this.section.widgets, () => html`
					<div class="widget" style="z-index: ${zindex - 1}">${this.section.widgets}</div>
				`)}
				${when(this.basicSpcAttributes?.length > 0, () => html`
					${repeat(this.basicSpcAttributes,
						(spcAttribute) => spcAttribute.key,
						// 'colorSelect','compactSelect', 'comboSelect', 'reducedSelect', 'reducedChoiceSelect'
						(spcAttribute, index) => html`
							<div class="attribute" style="z-index: ${zindex - (2 + index)}">
								<spc-attribute
									.attribute=${spcAttribute}
									.hideDisabledValues=${this.section.attributeOptions?.[spcAttribute.key]?.hideDisabledValues}
									.displayStyle=${this.section.attributeOptions?.[spcAttribute.key]?.displayStyle}
									.addon=${this.section.attributeOptions?.[spcAttribute.key]?.addon}>
								</spc-attribute>
							</div>
						`
					)}
				`)}
				${when(this.section.widgetsBasic, () => html`
					<div class="attribute" style="z-index: ${zindex - (this.basicSpcAttributes?.length)}">${this.section.widgetsBasic}</div>
				`)}
				${when(this.detailSpcAttributes?.length > 0, () => html`
					<div class="header" style="z-index: ${zindex - this.basicSpcAttributes?.length}">${msg('Detail Specsheet')}</div>
					${repeat(this.detailSpcAttributes,
						(spcAttribute) => spcAttribute.key,
						// 'colorSelect','compactSelect', 'comboSelect', 'reducedSelect', 'reducedChoiceSelect'
						(spcAttribute, index) => html`
							<div class="attribute" style="z-index: ${zindex - (this.basicSpcAttributes?.length + index)}">
								<spc-attribute
									.attribute=${spcAttribute}
									.hideDisabledValues=${this.section.attributeOptions?.[spcAttribute.key]?.hideDisabledValues}
									.displayStyle=${this.section.attributeOptions?.[spcAttribute.key]?.displayStyle}
									.addon=${this.section.attributeOptions?.[spcAttribute.key]?.addon}>
								</spc-attribute>
							</div>
						`
					)}
				`)}
				${when(this.unsatistfiedSpcAttributes?.length > 0 && this.detailPercentage >= 100 && this.basicPercentage >= 100, () => html`
					<div class="header" style="z-index: ${zindex - (this.basicSpcAttributes?.length + this.detailSpcAttributes?.length)}">${msg('Unsatisfied Attributes')}</div>
					${repeat(this.unsatistfiedSpcAttributes,
						(spcAttribute) => spcAttribute.key,
						(spcAttribute, index) => html`
							<div class="attribute" style="z-index: ${zindex - (this.basicSpcAttributes?.length + this.detailSpcAttributes?.length) + index}">
								<spc-attribute
									.attribute=${spcAttribute}
									.hideDisabledValues=${this.section.attributeOptions?.[spcAttribute.key]?.hideDisabledValues}
									.displayStyle=${this.section.attributeOptions?.[spcAttribute.key]?.displayStyle}
									.addon=${this.section.attributeOptions?.[spcAttribute.key]?.addon}>
								</spc-attribute>
							</div>
						`
					)}
				`)}

				${when(this.section.widgetsEnd, () => html`
					<div class="attribute" style="z-index: ${zindex - (this.basicSpcAttributes?.length + this.detailSpcAttributes?.length)}">
						${this.section.widgetsEnd}
					</div>
				`)}

			</div>
        `;
	}
}
